import React, { CSSProperties } from "react";
import { getButtonSizeStyles, getButtonVariantStyles, ButtonSize, ButtonVariant } from "../../../config/styleConfig";

interface ButtonExportProps {
  text?: string | Record<string, string>;
  link?: string;
  linkType?: "external" | "internal" | "phone" | "email";
  variant?: ButtonVariant;
  size?: ButtonSize;
  borderRadius?: number;
  borderColor?: string;
  borderWidth?: number;
  fullWidth?: boolean;
  fontFamily?: string;
  backgroundColor?: string;
  textColor?: string;
  target?: "_self" | "_blank";
  hideOnDesktop?: boolean;
  hideOnMobile?: boolean;
}

export const ButtonExport: React.FC<ButtonExportProps> = ({
  text = "Button",
  link = "#",
  linkType = "external",
  variant = "primary",
  size = "md",
  borderRadius = 8,
  borderColor,
  borderWidth,
  fullWidth = false,
  fontFamily,
  backgroundColor,
  textColor,
  target = "_self",
  hideOnDesktop = false,
  hideOnMobile = false,
}) => {
  const sizeStyles = getButtonSizeStyles(size, false);
  const variantStyles = getButtonVariantStyles(
    variant,
    "var(--color-primary, #3b82f6)",
    "var(--color-secondary, #10b981)",
    backgroundColor,
    textColor
  );

  const getHref = () => {
    switch (linkType) {
      case "phone":
        return `tel:${link.replace(/\s/g, "")}`;
      case "email":
        return `mailto:${link}`;
      case "internal":
        return link.startsWith("/") ? link : `/${link}`;
      default:
        return link;
    }
  };

  const style: CSSProperties = {
    display: fullWidth ? "block" : "inline-block",
    width: fullWidth ? "100%" : "auto",
    padding: `${sizeStyles.py}px ${sizeStyles.px}px`,
    fontSize: `${sizeStyles.fontSize}px`,
    ...(fontFamily
      ? { fontFamily: `'${fontFamily}', var(--font-family), sans-serif` }
      : typeof text === "string" && !/[\u0E00-\u0E7F]/.test(text)
        ? { fontFamily: "var(--font-family-en, var(--font-family))" }
        : typeof text === "object" && !/[\u0E00-\u0E7F]/.test((text as Record<string, string>).th || "")
          ? { fontFamily: "var(--font-family-en, var(--font-family))" }
          : {}),
    fontWeight: 500,
    textAlign: "center",
    textDecoration: "none",
    borderRadius: `${borderRadius}px`,
    background: variantStyles.background,
    color: variantStyles.color,
    border: borderColor && borderWidth
      ? `${borderWidth}px solid ${borderColor}`
      : variantStyles.border,
    cursor: "pointer",
    transition: "opacity 0.2s",
    boxSizing: "border-box",
  };

  const actualTarget = linkType === "external" ? target : "_self";
  const rel = actualTarget === "_blank" ? "noopener noreferrer" : undefined;

  const hideAttrs: Record<string, string> = {};
  if (hideOnDesktop) hideAttrs["data-hide-on-desktop"] = "true";
  if (hideOnMobile) hideAttrs["data-hide-on-mobile"] = "true";

  if (typeof text === "object") {
    const obj = text as Record<string, string>;
    const displayText = obj.th || obj.en || "";
    return (
      <a href={getHref()} style={style} target={actualTarget} rel={rel} {...hideAttrs} data-i18n-th={obj.th || ""} data-i18n-en={obj.en || ""} className="i18n-text">
        {displayText}
      </a>
    );
  }

  return (
    <a href={getHref()} style={style} target={actualTarget} rel={rel} {...hideAttrs}>
      {text}
    </a>
  );
};
